import { Checkbox, Spinner } from '@amelio/ui-library';

import { useGetCompanyValues } from '@/api/company-values';
import { useAmelioTranslations } from '@/hooks';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { getIconSvg } from '@/utils/iconSvgs';
import { getLocalizedText } from '@/utils/localization';

/** The glyph the vision and values page draws for a value saved without one. */
const DEFAULT_VALUE_ICON = 'heart';

interface CompanyValuesStepProps {
    includeCompanyValues: boolean;
    onToggle: (checked: boolean) => void;
    dataTestId?: string;
}

export const CompanyValuesStep = ({
    includeCompanyValues,
    onToggle,
    dataTestId = 'section-company-values',
}: CompanyValuesStepProps) => {
    const { t } = useAmelioTranslations();
    const { data: companyValues = [], isLoading, error } = useGetCompanyValues();
    const activeCompanyValues = companyValues
        .filter((cv) => cv.status === 'Active')
        .sort((a, b) => a.sortOrder - b.sortOrder);

    // "No values" is a statement about the company, not a display state: said before the answer
    // arrives, or after it failed, it is a wrong answer the manager acts on -- they carry on and
    // launch an assessment without the values it should have carried. Same three states the
    // vision and values page draws for this very list.
    const renderValues = () => {
        if (isLoading) {
            return <Spinner dataTestId={'company-values-loader'} />;
        }

        if (error) {
            return (
                <p
                    role={'alert'}
                    className={'text-caption text-red-500'}
                >
                    {t('visionValues.errorLoadingValues')}
                </p>
            );
        }

        if (activeCompanyValues.length === 0) {
            return (
                <p className={'text-xs text-gray-500 italic'}>
                    {t('managerAssessmentSetup.companyValues.noValues')}
                </p>
            );
        }

        return activeCompanyValues.map((value) => (
            <div
                key={value.id}
                className={
                    'flex items-center gap-4 rounded-xl border border-white bg-white/80 px-6 py-4'
                }
                data-testid={`company-value-${value.id}`}
            >
                <div
                    className={
                        'flex size-10 shrink-0 items-center justify-center rounded-lg shadow-sm'
                    }
                    style={{
                        background: resolveCompanyValueIconBackground(
                            value.icon,
                            value.iconBackground,
                        ),
                    }}
                    aria-hidden
                >
                    {getIconSvg(value.icon || DEFAULT_VALUE_ICON, 22)}
                </div>

                <div className={'flex-1 overflow-hidden'}>
                    <h4 className={'text-sm font-medium text-gray-900'}>
                        {getLocalizedText(value.name)}
                    </h4>

                    {value.description && (
                        <p className={'mt-1 text-[12px] text-gray-400'}>
                            {getLocalizedText(value.description)}
                        </p>
                    )}
                </div>
            </div>
        ));
    };

    return (
        <div
            className={'flex flex-col gap-6'}
            data-testid={dataTestId}
        >
            <div
                className={
                    'flex flex-col gap-6 rounded-2xl border border-white bg-white/90 p-6 md:!p-[53px]'
                }
            >
                <p className={'max-w-[667px] !text-sm font-medium text-gray-900'}>
                    {t('managerAssessmentSetup.companyValues.description')}
                </p>

                <div className={'[&_label]:font-medium [&_label]:hover:text-teal-400'}>
                    <Checkbox
                        checked={includeCompanyValues}
                        onChange={onToggle}
                        label={t('managerAssessmentSetup.companyValues.include')}
                        dataTestId={'include-company-values'}
                    />
                </div>

                {includeCompanyValues && (
                    <div className={'mt-2 flex flex-col gap-3'}>{renderValues()}</div>
                )}
            </div>
        </div>
    );
};
